import { Alert } from "@interstate/components/Alert";
import { useState } from "react";
import styled from "styled-components";

const DetailsControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 4px;
`;

const CopyButton = styled.button`
  background: none;
  border: 1px solid currentColor;
  border-radius: 3px;
  padding: 1px 7px;
  color: inherit;
  cursor: pointer;
  font-size: 0.75em;
  opacity: 0.75;
  &:hover {
    opacity: 1;
  }
`;

const ErrorContainer = styled.div`
  padding: 16px;
  max-width: 100%;
  box-sizing: border-box;

  /* Force MUI Alert components to respect container width */
  .MuiPaper-root,
  .MuiAlert-root {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const ErrorMessage = styled.div`
  font-weight: 500;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const ShowDetailsLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.85em;
  opacity: 0.8;
`;

const DetailsTable = styled.table`
  margin-top: 6px;
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75em;
  word-break: break-all;
`;

const DetailsRow = styled.tr`
  vertical-align: top;
`;

const DetailsLabel = styled.td`
  font-weight: 600;
  white-space: nowrap;
  padding: 1px 10px 1px 0;
  width: 1%;
`;

const DetailsValue = styled.td`
  opacity: 0.9;
  word-break: break-all;
`;

export interface ErrorDetails {
  url?: string;
  statusCode?: number;
  response?: string;
  dealerId?: number | string;
  consumerId?: string;
}

export interface ErrorAlertProps {
  message: string;
  testId?: string;
  details?: ErrorDetails;
}

export function ErrorAlert({ message, testId, details }: ErrorAlertProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasDetails =
    details &&
    (details.url ||
      details.statusCode !== undefined ||
      details.response ||
      details.dealerId !== undefined ||
      details.consumerId);

  const handleCopy = () => {
    if (!details) return;
    const lines: string[] = [];
    if (details.url) lines.push(`Request URL: ${details.url}`);
    if (details.statusCode !== undefined)
      lines.push(`Status code: ${details.statusCode}`);
    if (details.response) lines.push(`Response: ${details.response}`);
    if (details.dealerId !== undefined)
      lines.push(`Dealer ID: ${details.dealerId}`);
    if (details.consumerId) lines.push(`Consumer ID: ${details.consumerId}`);
    const text = lines.join("\n");
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {
          execCommandCopy(text);
        });
    } else {
      execCommandCopy(text);
    }
  };

  const execCommandCopy = (text: string) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      if (document.execCommand("copy")) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } finally {
      document.body.removeChild(textarea);
    }
  };

  return (
    <ErrorContainer data-testid={testId}>
      <Alert role="alert" type="error">
        <ErrorMessage>{message}</ErrorMessage>
        {hasDetails && (
          <DetailsControls>
            <ShowDetailsLink
              data-testid="error-alert-show-details-link"
              onClick={() => setShowDetails(prev => !prev)}
            >
              {showDetails ? "hide details" : "show details"}
            </ShowDetailsLink>
            {showDetails && (
              <CopyButton
                data-testid="error-alert-copy-to-clipboard-button"
                onClick={handleCopy}
              >
                {copied ? "copied!" : "copy to clipboard"}
              </CopyButton>
            )}
          </DetailsControls>
        )}
        {hasDetails && showDetails && details && (
          <DetailsTable>
            <tbody>
              {details.url && (
                <DetailsRow>
                  <DetailsLabel>Request URL:</DetailsLabel>
                  <DetailsValue>{details.url}</DetailsValue>
                </DetailsRow>
              )}
              {details.statusCode !== undefined && (
                <DetailsRow>
                  <DetailsLabel>Status code:</DetailsLabel>
                  <DetailsValue>{details.statusCode}</DetailsValue>
                </DetailsRow>
              )}
              {details.response && (
                <DetailsRow>
                  <DetailsLabel>Response:</DetailsLabel>
                  <DetailsValue>{details.response}</DetailsValue>
                </DetailsRow>
              )}
              {details.dealerId !== undefined && (
                <DetailsRow>
                  <DetailsLabel>Dealer ID:</DetailsLabel>
                  <DetailsValue>{details.dealerId}</DetailsValue>
                </DetailsRow>
              )}
              {details.consumerId && (
                <DetailsRow>
                  <DetailsLabel>Consumer ID:</DetailsLabel>
                  <DetailsValue>{details.consumerId}</DetailsValue>
                </DetailsRow>
              )}
            </tbody>
          </DetailsTable>
        )}
      </Alert>
    </ErrorContainer>
  );
}
